// packages
import { intcomma } from 'journalize';
import { pointer, select } from 'd3-selection';
import { scaleLinear, scaleUtc } from 'd3-scale';
import { groups as grouper, max, extent } from 'd3-array';
import { line as createLine } from 'd3-shape';

// utils
import { last } from '../../utils/last';
import { createBase } from '../../utils/d3-base';
import { closestPoint } from '../../utils/closest-point';
import { monthTickValues, monthFormat } from '../../utils/d3-utils';
import { __ } from '../../utils/i18n';

// our container of choice
export function boot(data, id = 'demographics-timeseries-chart') {
  const container = select(`#${id}`);
  const raceList = ['black', 'asian', 'latino', 'white', 'other'];
  data = data.filter((d) => raceList.includes(d.race));
  const groupedData = grouper(data, (d) => d.race);

  render();
  function render() {
    // first empty the container on render every time
    container.html('');

    // create our base pieces
    const { g, x, y, width, height, xAxis, yAxis, drawAxis } = createBase({
      container,
      height: 300,
      x: scaleUtc(),
      y: scaleLinear(),
      margin: { bottom: 35, left: 45, right: 70 },
      title: __('Cases by race per 100,000 residents'),
    });

    // set up our x scale
    x.domain(extent(data, (d) => d.date)).range([0, width]);

    const yMax = max(data.map((d) => d.cases_per_100k));
    y.domain([0, yMax * 1.1]);

    // tell the x axis we only want these ticks
    const startDate = data[0].date;
    const endDate = last(data).date;
    xAxis
      .tickValues(monthTickValues(startDate, endDate))
      .tickFormat(monthFormat);

    // tell the y axis what we want
    yAxis.tickSize(-width).ticks(5);

    drawAxis();

    const groups = g.append('g').attr('class', 'groups');

    const group = groups
      .selectAll('g.group')
      .data(groupedData)
      .join('g')
      .attr('data-race', (d) => d[0])
      .attr(
        'class',
        (d) => `group ${d[0] === 'latino' ? 'active' : 'inactive'}`
      );

    const drawLine = createLine()
      .x((d) => x(d.date))
      .y((d) => y(d.cases_per_100k));

    const line = group
      .append('path')
      .attr('class', 'line line--halo')
      .attr('role', 'graphics-symbol')
      .attr('d', (d) => drawLine(d[1]));

    // ...then clone 'em and de-halo for the top layer
    line.clone(true).classed('line--halo', false);

    // place and space our label halos...
    const annoLayer = g
      .selectAll('g.anno')
      .data(groupedData)
      .join('g')
      .attr('data-race', (d) => d[0])
      .attr('class', (d) => `anno ${d[0] === 'latino' ? 'active' : ''}`)
      .attr(
        'transform',
        (d) => `translate(${x(endDate)}, ${y(last(d[1]).cases_per_100k)})`
      );

    annoLayer
      .append('circle')
      .attr('class', (d) => (d[0] == 'latino' ? 'active' : ''))
      .attr('r', (d) => (d[0] == 'latino' ? 4 : 2.5));

    const labelCrosswalk = {
      asian: __('Asian'),
      black: __('Black'),
      latino: __('Latino'),
      white: __('White'),
      other: __('Other'),
    };
    const labelYOffsets = {
      black: -8.5,
      white: 3,
      asian: 6,
      other: -6,
    };
    const label = annoLayer
      .append('text')
      .attr('class', `label label--halo`)
      .attr('text-anchor', 'start')
      .attr('dx', 6);

    label
      .append('tspan')
      .attr('dy', (d) => 4 + (labelYOffsets[d[0]] || 0))
      .attr('x', 0)
      .attr('class', 'race-label')
      .text((d) => labelCrosswalk[d[0]]);
    label
      .append('tspan')
      .attr('dy', 12.5)
      .attr('x', 6)
      .attr('class', 'data-label')
      .text(
        (d) => `${intcomma(last(d[1]).cases_per_100k.toFixed())} ` + __('cases')
      );
    label
      .append('tspan')
      .attr('dy', 12.5)
      .attr('x', 6)
      .attr('class', 'data-label')
      .text(__('per 100,000'));

    label.clone(true).classed('label--halo', false);

    const text = g.selectAll('text.label');

    const overlay = g
      .append('rect')
      .attr('class', 'overlay')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', width)
      .attr('height', height);

    overlay
      .on('mouseenter', moved)
      .on('mousemove', moved)
      .on('mouseleave', left);

    function moved(event) {
      const coords = pointer(event);

      let closestDistance = Infinity;
      let closestGroup = null;

      group.each((_, i, nodes) => {
        const node = nodes[i];
        const l = node.querySelector('.line');

        const { distance } = closestPoint(l, coords);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestGroup = node;
        }
      });

      if (closestDistance < 80) {
        g.classed('has-interaction', true);

        const sel = select(closestGroup).datum();
        const name = sel[0];

        // highlight and raise the active one
        group.classed('active', false);
        group
          .classed('active', (_, i, nodes) => closestGroup === nodes[i])
          .filter('.active')
          .raise();

        annoLayer.classed('active', false);
        annoLayer.selectAll('text').classed('display-none', (d) => {
          return d[0] != name;
        });
        annoLayer.classed('active', (d) => {
          return d[0] === name;
        });
        annoLayer
          .selectAll('circle')
          .attr('r', (d) => (d[0] == name ? 4 : 2.5));
        annoLayer.filter('.active').raise();
      } else {
        left();
      }
    }

    function left() {
      // disable interaction
      g.classed('has-interaction', false);
      group.classed('active', false);
      annoLayer.classed('active', false);
      annoLayer.selectAll('text').classed('display-none', false);
      annoLayer.selectAll('circle').attr('r', 2.5);
      reset();
    }

    function reset() {
      group.classed('active', (d) => {
        return d[0] === 'latino';
      });
      annoLayer.classed('active', (d) => {
        return d[0] === 'latino';
      });
      annoLayer
        .selectAll('circle')
        .attr('r', (d) => (d[0] == 'latino' ? 4 : 2.5));
    }
  }
  return render;
}
